import * as React from "react";
import { Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box, Container, Stack, Typography, Button, Divider,
  IconButton, Drawer, List, ListItemButton, ListItemText,
  AppBar, Toolbar, useMediaQuery, Menu, Tooltip, MenuItem
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import { adminFetch } from "../../services/adminFetch";

export default function AdminLayout() {
  const { locale } = useParams<{ locale: string }>();
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();
  const isActive = (path: string) => loc.pathname.startsWith(`/${locale || "el"}${path}`);

  const go = (path: string) => {
    setOpen(false);
    nav(`/${locale || "el"}${path}`);
  };

  const logout = async () => {
    await adminFetch("/api/admin/auth/logout", { method: "POST" });
    nav(`/${locale || "el"}/admin/login`, { replace: true });
  };

  const menuGroups = [
    {
      label: "Properties",
      items: [
        { label: "All", path: "/admin/properties" },
        { label: "New", path: "/admin/new-property" },
        { label: "Features", path: "/admin/features" },
      ],
    },
    {
      label: "Availability",
      items: [{ label: "Search", path: "/admin/availability" }],
    },
    {
      label: "Bookings",
      items: [
        { label: "All", path: "/admin/bookings" },
        { label: "Calendar", path: "/admin/bookings/calendar" },
        { label: "New", path: "/admin/bookings/new" },
        { label: "Booking inquiries", path: "/admin/booking-inquiries" },
      ],
    },
    {
      label: "Messages",
      items: [{ label: "All", path: "/admin/messages" }],
    },
  ];

  const [bookingsAnchor, setBookingsAnchor] = React.useState<null | HTMLElement>(null);
  const bookingsOpen = Boolean(bookingsAnchor);

  const bookingsGroup = menuGroups.find((g) => g.label === "Bookings");

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ py: 1, gap: 2 }}>
            {isMobile ? (
              <IconButton onClick={() => setOpen(true)} edge="start">
                <MenuIcon />
              </IconButton>
            ) : null}

            <Typography sx={{ fontWeight: 900, flexGrow: 1 }}>
              Ritas Properties · Admin
            </Typography>

            {!isMobile ? (
              <Stack direction="row" spacing={1} alignItems="center">
                {menuGroups.map((g) => {
                  const groupActive = g.items.some((it) => isActive(it.path));

                  // ειδικό dropdown για Bookings
                  if (g.label === "Bookings" && bookingsGroup) {
                    return (
                      <React.Fragment key={g.label}>
                        <Button
                          variant={groupActive ? "contained" : "text"}
                          onClick={(e) => setBookingsAnchor(e.currentTarget)}
                          endIcon={<KeyboardArrowDownIcon />}
                          sx={{ fontWeight: 900, borderRadius: 2 }}
                        >
                          Bookings
                        </Button>

                        <Menu
                          anchorEl={bookingsAnchor}
                          open={bookingsOpen}
                          onClose={() => setBookingsAnchor(null)}
                        >
                          {bookingsGroup.items.map((it) => {
                            const active = isActive(it.path);
                            return (
                              <MenuItem
                                key={it.path}
                                selected={active}
                                onClick={() => {
                                  setBookingsAnchor(null);
                                  go(it.path);
                                }}
                              >
                                {it.label}
                              </MenuItem>
                            );
                          })}
                        </Menu>
                      </React.Fragment>
                    );
                  }

                  // τα άλλα groups πάνε στο default
                  return (
                    <Button
                      key={g.label}
                      variant={groupActive ? "contained" : "text"}
                      onClick={() => go(g.items[0].path)}
                      sx={{ fontWeight: 900, borderRadius: 2 }}
                    >
                      {g.label}
                    </Button>
                  );
                })}


              </Stack>
            ) : null}

            <Button variant="outlined" onClick={logout}>Logout</Button>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 280, p: 1 }}>
          <Typography sx={{ fontWeight: 900, p: 2 }}>Menu</Typography>
          <Divider />
          <List>
            {menuGroups.map((g) => (
              <Box key={g.label} sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ px: 2, pt: 1.5, pb: 0.5, fontWeight: 900, letterSpacing: 0.6 }}
                  color="text.secondary"
                >
                  {g.label.toUpperCase()}
                </Typography>

                {g.items.map((it) => (
                  <ListItemButton key={it.path} onClick={() => go(it.path)} sx={{ pl: 3 }}>
                    <ListItemText primary={it.label} />
                  </ListItemButton>
                ))}
              </Box>
            ))}
          </List>

        </Box>
      </Drawer>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Outlet />
      </Container>
    </Box>
  );
}
